import { Loader2, Plus } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { PolicyFormModal } from '@/components/document-policies/PolicyFormModal';
import { PolicyHistoryModal } from '@/components/document-policies/PolicyHistoryModal';
import { PolicyTable } from '@/components/document-policies/PolicyTable';
import { createPolicy, listPolicies, updatePolicy } from '@/services/documentPolicyService';

import type { DocumentPolicy, DocumentPolicyFormData } from '@/types/documentPolicy.types';

type ModalState =
  | { isOpen: false }
  | { isOpen: true; mode: 'create' }
  | { isOpen: true; mode: 'edit'; policy: DocumentPolicy }
  | { isOpen: true; mode: 'clone'; policy: DocumentPolicy };

export function DocumentPoliciesPage() {
  const { t } = useTranslation();
  const [policies, setPolicies] = useState<DocumentPolicy[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [roleFilter, setRoleFilter] = useState('');
  const [siteFilter, setSiteFilter] = useState('');
  const [modal, setModal] = useState<ModalState>({ isOpen: false });
  const [historyPolicy, setHistoryPolicy] = useState<DocumentPolicy | null>(null);

  const loadPolicies = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await listPolicies();
      setPolicies(data);
    } catch {
      setError(t('documentPolicies.errorLoading'));
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void loadPolicies();
  }, [loadPolicies]);

  const filteredPolicies = useMemo(() => {
    return policies.filter((p) => {
      if (roleFilter && p.role !== roleFilter) return false;
      if (siteFilter && p.site !== siteFilter) return false;
      return true;
    });
  }, [policies, roleFilter, siteFilter]);

  const handleSave = useCallback(
    async (data: DocumentPolicyFormData) => {
      try {
        if (modal.isOpen && modal.mode === 'edit') {
          await updatePolicy(modal.policy.policyId, data);
          toast.success(t('documentPolicies.toasts.updateSuccess'));
        } else {
          await createPolicy(data);
          toast.success(
            modal.isOpen && modal.mode === 'clone'
              ? t('documentPolicies.toasts.cloneSuccess')
              : t('documentPolicies.toasts.createSuccess'),
          );
        }
        setModal({ isOpen: false });
        await loadPolicies();
      } catch {
        toast.error(t('documentPolicies.toasts.error'));
      }
    },
    [modal, loadPolicies, t],
  );

  return (
    <div className='space-y-6'>
      {/* Filters */}
      <div className='flex flex-wrap items-center gap-4 rounded-lg bg-white px-4 py-3 shadow'>
        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          className='rounded-md border border-gray-300 bg-white pl-3 pr-8 py-2 text-sm text-gray-700 focus:border-primary focus:ring-1 focus:ring-primary outline-none'>
          <option value=''>{t('documentPolicies.filters.allRoles')}</option>
          <option value='armed'>{t('jobPostings.roles.armed')}</option>
          <option value='unarmed'>{t('jobPostings.roles.unarmed')}</option>
          <option value='supervisor'>{t('jobPostings.roles.supervisor')}</option>
          <option value='patrol'>{t('jobPostings.roles.patrol')}</option>
          <option value='event'>{t('jobPostings.roles.event')}</option>
        </select>
        <select
          value={siteFilter}
          onChange={(e) => setSiteFilter(e.target.value)}
          className='rounded-md border border-gray-300 bg-white pl-3 pr-8 py-2 text-sm text-gray-700 focus:border-primary focus:ring-1 focus:ring-primary outline-none'>
          <option value=''>{t('documentPolicies.filters.allSites')}</option>
          <option value='_all'>{t('documentPolicies.allSites')}</option>
          <option value='FL'>FL</option>
          <option value='NY'>NY</option>
        </select>

        <button
          onClick={() => setModal({ isOpen: true, mode: 'create' })}
          className='ml-auto inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700'>
          <Plus className='h-4 w-4' />
          {t('documentPolicies.actions.create')}
        </button>
      </div>

      {/* Error */}
      {error && <div className='rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700'>{error}</div>}

      {/* Loading */}
      {isLoading && !error && (
        <div className='flex justify-center py-12'>
          <Loader2 className='h-8 w-8 animate-spin text-blue-600' />
        </div>
      )}

      {/* Table */}
      {!isLoading && !error && (
        <div className='overflow-hidden rounded-lg bg-white shadow'>
          <PolicyTable
            policies={filteredPolicies}
            isLoading={isLoading}
            onEdit={(policy) => setModal({ isOpen: true, mode: 'edit', policy })}
            onClone={(policy) => setModal({ isOpen: true, mode: 'clone', policy })}
            onHistory={(policy) => setHistoryPolicy(policy)}
          />
        </div>
      )}

      {/* Modal */}
      <PolicyFormModal
        isOpen={modal.isOpen}
        onClose={() => setModal({ isOpen: false })}
        onSave={handleSave}
        mode={modal.isOpen ? modal.mode : 'create'}
        policy={modal.isOpen && (modal.mode === 'edit' || modal.mode === 'clone') ? modal.policy : undefined}
      />

      {historyPolicy ? (
        <PolicyHistoryModal
          isOpen={!!historyPolicy}
          onClose={() => setHistoryPolicy(null)}
          policyId={historyPolicy.policyId}
          policyName={historyPolicy.name}
        />
      ) : null}
    </div>
  );
}
